import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { helpCenterPageFooter } from './layout/footerConfigs'
import { SEOHead } from './SEO/SEOHead'
import { getStructuredData } from '../config/structuredData'

export const HelpCenterPage: React.FC = () => {
  const isMobile = useIsMobile()
  const [searchQuery, setSearchQuery] = useState('')

  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Use optimized scroll hook to prevent flickering
  const { scrollProgress } = useOptimizedScroll()

  return (
    <>
      {/* SEO Head */}
      <SEOHead
        customTitle="Help Center - Mernaki | Support & Resources"
        customDescription="Get help with Mernaki services. Find answers to common questions, tutorials, and support resources."
        customMetadata={{
          structuredData: getStructuredData('help')
        }}
      />

      {/* Navbar is now global - no individual page navbars needed */}


      <motion.div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: '48px',
          borderBottomRightRadius: '48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transform: `translateY(${scrollProgress * -20}px)`
        }}
        transition={{ duration: 0 }}
      >
        {/* Randomly Moving Gradient Orbs */}
        <motion.div
          className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            top: '15%',
            left: '8%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, -80, 60, 0],
            y: [0, 70, -50, 0],
            scale: [1, 1.3, 0.85, 1]
          }}
          transition={{
            duration: 27,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
        <motion.div
          className="absolute w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
            top: '50%',
            right: '5%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, 90, -70, 0],
            y: [0, -60, 80, 0],
            scale: [1, 0.9, 1.2, 1]
          }}
          transition={{
            duration: 32,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
        <motion.div
          className="absolute w-72 h-72 rounded-full opacity-15 blur-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            bottom: '15%',
            left: '12%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, 70, -90, 0],
            y: [0, -80, 50, 0],
            scale: [1, 1.15, 0.9, 1]
          }}
          transition={{
            duration: 29,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        <CategorySection prefersReducedMotion={prefersReducedMotion.current} />
        <FeaturedArticlesSection prefersReducedMotion={prefersReducedMotion.current} />
        <FAQSection prefersReducedMotion={prefersReducedMotion.current} />
        <ContactSection prefersReducedMotion={prefersReducedMotion.current} />
      </motion.div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}

interface SearchResult {
  id: string
  title: string
  description: string
  category: 'Docs' | 'Product' | 'Support' | 'API'
  link: string
}

const searchIndex: SearchResult[] = [
  { id: '1', title: 'JobMeet Product Guide', description: 'Learn about JobMeet features, screenshots, and more.', category: 'Docs', link: '/products/jobmeet' },
  { id: '2', title: 'API Reference', description: 'Full API documentation for developers and integrators.', category: 'API', link: '/developer/api' },
  { id: '3', title: 'Project Crimson', description: 'Development environment and tooling solutions.', category: 'Product', link: '/codered' },
  { id: '4', title: 'JobMeet API Docs', description: 'Specific method documentation for the JobMeet API.', category: 'API', link: '/api/jobmeet' },
  { id: '5', title: 'Contact Support', description: 'Get help with billing, account issues, or general inquiries.', category: 'Support', link: '/contact' },
  { id: '6', title: 'MacX Handheld', description: 'Explore our latest handheld gaming device.', category: 'Product', link: '/macx-handheld' },
  { id: '7', title: 'Documentation Hub', description: 'Central hub for all platform documentation and guides.', category: 'Docs', link: '/docs' },
  { id: '8', title: 'Report an Issue', description: 'Submit a bug report or technical issue to our team.', category: 'Support', link: '/report-issue' },
]

const HeroSection: React.FC<{ prefersReducedMotion: boolean; searchQuery: string; setSearchQuery: (q: string) => void }> = ({ prefersReducedMotion, searchQuery, setSearchQuery }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })
  const [showResults, setShowResults] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const filteredResults = React.useMemo(() => {
    if (!searchQuery.trim()) return []
    const query = searchQuery.toLowerCase()
    return searchIndex.filter(item =>
      item.title.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query)
    ).slice(0, 5) // Limit to 5 results
  }, [searchQuery])

  return (
    <section ref={ref} className="relative pt-48 pb-32 overflow-visible" style={{ backgroundColor: 'transparent', zIndex: 20 }}>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1 className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>
          We're Here to Help
        </motion.h1>
        <motion.p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed mb-12" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}>
          Find answers, browse guides, or contact our support team.
        </motion.p>

        <motion.div className="max-w-2xl mx-auto relative" initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.5, ease: [0.4, 0.0, 0.2, 1] }}>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search guides, tutorials, or FAQs…"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setShowResults(true)
              }}
              onFocus={() => setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)} // Delay to allow click
              className="w-full px-6 py-4 pr-12 rounded-2xl text-lg transition-all duration-300 shadow-sm focus:shadow-md"
              style={{
                backgroundColor: '#FFFFFF',
                border: '2px solid rgba(0, 0, 0, 0.1)',
                color: '#1A1A1A',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif', outline: 'none'"
              }}
              onInput={(e) => e.currentTarget.style.borderColor = '#4A90E2'}
            />
            <svg className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6" style={{ color: '#999999' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Search Results Dropdown */}
          <AnimatePresence>
            {showResults && searchQuery.trim() && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="absolute w-full mt-4 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden text-left z-50 text-base"
              >
                {filteredResults.length > 0 ? (
                  <ul>
                    {filteredResults.map((result) => (
                      <li key={result.id} className="border-b border-gray-50 last:border-none">
                        <Link
                          to={result.link}
                          className="block p-4 hover:bg-gray-50 transition-colors group"
                          onClick={() => setShowResults(false)}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                              {result.title}
                            </h4>
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full 
                              ${result.category === 'Docs' ? 'bg-purple-100 text-purple-700' :
                                result.category === 'API' ? 'bg-red-100 text-red-700' :
                                  result.category === 'Product' ? 'bg-blue-100 text-blue-700' :
                                    'bg-green-100 text-green-700'}`}>
                              {result.category}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 line-clamp-1">{result.description}</p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <p>No results found for "{searchQuery}"</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  )
}

const CategorySection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })

  const categories = [
    { title: 'Mernaki Services', desc: 'Account setup, payments, invoices, cancellation', isBlurred: false },
    { title: 'Project Crimson', desc: 'Development environment and tooling solutions', isBlurred: true },
    { title: 'Project Velocity', desc: 'Gaming platform and launcher system', isBlurred: true },
    { title: 'Project Nexus', desc: 'Portable gaming hardware device', isBlurred: true },
    { title: 'Rare Earth Portal', desc: 'Permit upload, verification process, document formats', isBlurred: false },
    { title: 'VerpackG Löschung', desc: 'Registration, verification, refund process', isBlurred: false }
  ]

  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 className="text-4xl md:text-5xl mb-16 text-center" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>
          Browse by Product
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, index) => (
            <motion.div
              key={category.title}
              className="p-8 rounded-2xl transition-all duration-300"
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                cursor: category.isBlurred ? 'not-allowed' : 'pointer',
                userSelect: category.isBlurred ? 'none' : 'auto',
                pointerEvents: category.isBlurred ? 'none' : 'auto'
              }}
              initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
              onMouseEnter={(e) => {
                if (!category.isBlurred) {
                  e.currentTarget.style.transform = 'translateY(-4px)'
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)'
                }
              }}
              onMouseLeave={(e) => {
                if (!category.isBlurred) {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              <h3
                className="text-2xl mb-3"
                style={{
                  color: category.isBlurred ? 'rgba(26, 26, 26, 0.3)' : '#1A1A1A',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  filter: category.isBlurred ? 'blur(3px)' : 'none'
                }}
              >
                {category.title}
              </h3>
              <p
                className="text-base"
                style={{
                  color: category.isBlurred ? 'rgba(102, 102, 102, 0.3)' : '#666666',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400,
                  filter: category.isBlurred ? 'blur(3px)' : 'none'
                }}
              >
                {category.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const FeaturedArticlesSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })

  const articles = [
    { title: 'How to submit a refund request for VerpackG Löschung', updated: 'Updated 2 days ago', isBlurred: false },
    { title: 'Setting up your Project Crimson workspace on macOS', updated: 'Updated 1 week ago', isBlurred: true },
    { title: 'Installing the latest Project Velocity update', updated: 'Updated 3 days ago', isBlurred: true },
    { title: 'Applying for a Rare Earth export permit', updated: 'Updated 5 days ago', isBlurred: false }
  ]

  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 className="text-4xl md:text-5xl mb-16 text-center" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>
          Popular Topics
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {articles.map((article, index) => (
            <motion.div
              key={article.title}
              className="p-6 rounded-2xl transition-all duration-300"
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                cursor: article.isBlurred ? 'not-allowed' : 'pointer',
                userSelect: article.isBlurred ? 'none' : 'auto',
                pointerEvents: article.isBlurred ? 'none' : 'auto'
              }}
              initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
              onMouseEnter={(e) => {
                if (!article.isBlurred) {
                  e.currentTarget.style.transform = 'translateY(-4px)'
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)'
                }
              }}
              onMouseLeave={(e) => {
                if (!article.isBlurred) {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              <h3
                className="text-xl mb-2"
                style={{
                  color: article.isBlurred ? 'rgba(26, 26, 26, 0.3)' : '#1A1A1A',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  filter: article.isBlurred ? 'blur(3px)' : 'none'
                }}
              >
                {article.title}
              </h3>
              <p
                className="text-sm"
                style={{
                  color: article.isBlurred ? 'rgba(153, 153, 153, 0.3)' : '#999999',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400,
                  filter: article.isBlurred ? 'blur(3px)' : 'none'
                }}
              >
                {article.updated}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const FAQSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)

  const faqs = [
    { q: 'How do I contact Mernaki support?', a: 'You can reach our support team at support@mernaki.de or use the contact form at the bottom of this page. We typically respond within 24 hours during business days.' },
    { q: 'How do I update my billing information?', a: 'Log into your account dashboard, navigate to Settings > Billing, and update your payment method or billing address there.' },
    { q: 'How do I request a refund or cancel a service?', a: 'To request a refund, email support@mernaki.de with your order number and reason. Refunds are processed within 5-7 business days according to our refund policy.' },
    { q: 'How long does verification take for buyers?', a: 'Verification typically takes 1-3 business days. You will receive an email notification once your account has been verified.' },
    { q: 'Is my data secure with Mernaki?', a: 'Yes, we take data security seriously. All data is encrypted in transit and at rest, stored on secure EU servers, and we are fully GDPR compliant.' }
  ]

  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 className="text-4xl md:text-5xl mb-16 text-center" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>
          Frequently Asked Questions
        </motion.h2>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="rounded-2xl overflow-hidden"
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(0, 0, 0, 0.1)'
              }}
              initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
            >
              <button
                className="w-full p-6 text-left flex items-center justify-between transition-colors duration-300"
                onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                style={{ backgroundColor: expandedFaq === index ? '#F9FAFB' : 'transparent' }}
              >
                <h3 className="text-lg pr-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
                  {faq.q}
                </h3>
                <svg
                  className="w-6 h-6 flex-shrink-0 transition-transform duration-300"
                  style={{ color: '#666666', transform: expandedFaq === index ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedFaq === index && (
                <motion.div
                  className="px-6 pb-6"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-base leading-relaxed" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>
                    {faq.a}
                  </p>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const ContactSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          className="p-12 rounded-3xl"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2 className="text-3xl md:text-4xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
            Still Need Help?
          </h2>
          <p className="text-lg mb-8" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>
            Contact our support team — we usually respond within 24 hours.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <a
              href="mailto:support@mernaki.de"
              className="px-8 py-4 rounded-xl text-base transition-all duration-300"
              style={{
                backgroundColor: '#1A1A1A',
                color: '#FFFFFF',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-block'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333333'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1A1A1A'}
            >
              support@mernaki.de
            </a>
            <Link
              to="/contact"
              className="px-8 py-4 rounded-xl text-base transition-all duration-300"
              style={{
                backgroundColor: 'transparent',
                color: '#1A1A1A',
                border: '2px solid #1A1A1A',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-block'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1A1A1A'
                e.currentTarget.style.color = '#FFFFFF'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.color = '#1A1A1A'
              }}
            >
              Open Support Form
            </Link>
          </div>

          <div className="text-sm space-y-1" style={{ color: '#999999', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>
            <p>Business Hours: Mon–Fri, 9:00–18:00 CET</p>
            <p>Munich, Germany</p>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  if (isMobile) {
    return <PageMobileFooter {...helpCenterPageFooter} />
  }

  return (
    <footer ref={ref} className="relative" style={{ backgroundColor: '#0E0E0E', position: 'sticky', bottom: 0, zIndex: 1, paddingTop: '140px', marginTop: '-80px' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Footer Header */}
        <motion.div
          className="text-center mb-12 pb-8 border-b"
          style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h3
            className="text-2xl mb-2"
            style={{
              color: '#FFFFFF',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              letterSpacing: '-0.02em'
            }}
          >
            Help Center
          </h3>
          <p
            className="text-sm"
            style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 400
            }}
          >
            Support & resources
          </p>
        </motion.div>

        <div className="py-16">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
          >
            {/* Column 1 - Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                  alt="Mernaki Logo"
                  className="object-contain"
                  style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                  loading="lazy"
                />
                <h3
                  className="text-lg"
                  style={{
                    color: '#FFFFFF',
                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                    fontWeight: 400
                  }}
                >
                  Mernaki
                </h3>
              </div>
              <p
                className="text-sm leading-relaxed"
                style={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400
                }}
              >
                Empowering businesses with modern recruitment tools.
              </p>
            </div>

            {/* Column 2 - Explore */}
            <div>
              <h3
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Explore
              </h3>
              <ul className="space-y-3">
                <li>
                  <span
                    className="text-sm"
                    style={{
                      color: 'rgba(255, 255, 255, 0.3)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      filter: 'blur(2px)',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}
                  >
                    Project Crimson
                  </span>
                </li>
                <li>
                  <span
                    className="text-sm"
                    style={{
                      color: 'rgba(255, 255, 255, 0.3)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      filter: 'blur(2px)',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}
                  >
                    Project Velocity
                  </span>
                </li>
                <li>
                  <Link
                    to="/#services"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    Services
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/partnerships"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    Partnerships
                  </Link>
                </li>
                <li>
                  <span
                    className="text-sm"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400
                    }}
                  >
                    Press
                  </span>
                </li>
              </ul>
            </div>

            {/* Column 3 - Company */}
            <div>
              <h3
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Resources
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/docs"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Documentation
                  </Link>
                </li>
                <li>
                  <Link
                    to="/developer/api"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    API Reference
                  </Link>
                </li>
                <li>
                  <Link
                    to="/docs#how-it-works"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Guides
                  </Link>
                </li>
                <li>
                  <Link
                    to="/status"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    System Status
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 4 - Support */}
            <div>
              <h3
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Support
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/help"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/impressum"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Impressum
                  </Link>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Showcase Row - Flagship Projects */}
          <div className="mb-10 pb-10 border-b border-white/10">
            <div className="flex items-center justify-center gap-3 flex-wrap text-sm">
              <span
                className="text-sm"
                style={{
                  color: 'rgba(255, 255, 255, 0.3)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400,
                  filter: 'blur(2px)',
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}
              >
                Project Crimson
              </span>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
              <span
                className="text-sm"
                style={{
                  color: 'rgba(255, 255, 255, 0.3)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400,
                  filter: 'blur(2px)',
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}
              >
                Project Velocity
              </span>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
              <span
                className="text-sm"
                style={{
                  color: 'rgba(255, 255, 255, 0.3)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400,
                  filter: 'blur(2px)',
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}
              >
                Project Nexus
              </span>
            </div>
          </div>

          {/* Contact Line + Legal */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-10">
            <div className="flex items-center gap-4 text-sm">
              <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Munich, Germany</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>—</span>
              <a
                href="mailto:hello@mernaki.de"
                className="transition-all duration-300"
                style={{
                  color: 'rgba(255, 255, 255, 0.4)',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.4)'}
              >
                hello@mernaki.de
              </a>
            </div>
            <p
              className="text-sm"
              style={{
                color: 'rgba(255, 255, 255, 0.3)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 300
              }}
            >
              © {new Date().getFullYear()} Mernaki. Crafted in Germany.
            </p>
          </div>
        </div>

      </div>
    </footer>
  )
}
